// src/pages/AnalystLogin.tsx - FIXED with proper role-based redirects
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

type AnalystType = 'business' | 'problem_bank' | 'senior' | 'admin';

export default function AnalystLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, isLoading } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [analystType, setAnalystType] = useState<AnalystType>('business');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (loading || redirecting) return;
    if (isLoading) return;
    
    if (user) {
      setRedirecting(true);
      
      // Role-based redirects - THIS IS THE FIX
      if (user.role === 'admin') {
        navigate('/admin-portal', { replace: true });
      } else if (user.role === 'senior_analyst') {
        navigate('/senior-analyst', { replace: true });
      } else if (user.role === 'analyst') {
        navigate('/analyst-dashboard', { replace: true });
      } else {
        // Fallback for any other role
        const from = location.state?.from?.pathname || '/analyst-dashboard';
        navigate(from, { replace: true });
      }
    }
  }, [user, isLoading, navigate, location, loading, redirecting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setRedirecting(false);

    setTimeout(() => {
      let loginRole: string;
      let loginAnalystType: any = null;
      
      if (analystType === 'senior') {
        loginRole = 'senior_analyst';
      } else if (analystType === 'admin') {
        loginRole = 'admin';
      } else {
        loginRole = 'analyst';
        loginAnalystType = analystType;
      }
      
      const result = login(email, password, loginRole as any, loginAnalystType);
      
      if (result) {
        setError(result);
        setLoading(false);
      } else {
        setLoading(false);
        // The useEffect will handle redirect based on user role
      }
    }, 500);
  };

  const togglePasswordVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword(!showPassword);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pt-8 pb-6">
          <CardTitle className="text-3xl font-bold tracking-tight">Vett & Venture</CardTitle>
          <CardDescription className="text-sm mt-2">Analyst Portal</CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-5 px-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="analystType">Account Type</Label>
              <Select value={analystType} onValueChange={(value: AnalystType) => setAnalystType(value)}>
                <SelectTrigger id="analystType" className="h-11">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business Analyst</SelectItem>
                  <SelectItem value="problem_bank">Problem Bank Analyst</SelectItem>
                  <SelectItem value="senior">Senior Analyst</SelectItem>
                  <SelectItem value="admin">System Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setRememberMe(!rememberMe)}
                className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                  rememberMe 
                    ? 'bg-primary border-primary text-primary-foreground' 
                    : 'bg-white border-gray-300 hover:border-gray-400'
                }`}
              >
                {rememberMe && (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <Label 
                htmlFor="remember"
                className="text-sm font-medium cursor-pointer leading-none"
                onClick={() => setRememberMe(!rememberMe)}
              >
                Remember me
              </Label>
            </div>
          </CardContent>

          <CardFooter className="px-6 pb-8 pt-2">
            <Button 
              type="submit" 
              className="w-full h-11 font-medium"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}